import { useState } from "react";
import useCanvasDetection from "../../hooks/useCanvasDetection";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import { commonErrorToastMessage } from "../toastify/AllToastifyErrors";

const HelpingPointer = ({
  isSampleButtonClicked,
  content,
  handleTextAreaChange,
  handleUploadButtonClick,
  handleFileInputChange, 
  isFileUploaded,
  fileInputRef,
  Version = "",
  UserAttempts = 0,
}) => {
  const [showTooltip, setShowTooltip] = useState(true);
  const isCanvas = useCanvasDetection();

 const handleUploadLinkClick = () => {
    if (isSampleButtonClicked) return;
    // ✅ UserAttempts comes from prop — no localStorage.getItem needed
    if (UserAttempts <= 0) {
      commonErrorToastMessage("Quiz set counts Exhausted.");
      return;
    }
    handleUploadButtonClick();
    setShowTooltip(true);
  };

  return (
    <div
      className={`text-justify ${
        isCanvas
          ? "md:mt-[-44%] lg:mt-[-38%]"
          : "md:mt-[-40%] sm:mt-[-80%] lg:mt-[-30%]"
      } ${isSampleButtonClicked ? "" : ""}`}
      value={content}
      onChange={handleTextAreaChange}
      disabled={isFileUploaded}
    >
      {/* ✅ Version from prop — no async delay, always in sync with parent */}
      {Version === "Free" || Version === "free" ? (
        <div className="mt-[0.5rem] px-4">
          <div className="text-center text-[#777e8d]">
            <p className="font-bold text-[clamp(1.25rem,3vw,0.5rem)]">It's demo time.</p>
            <p className="text-[clamp(1.25rem,3vw,0.5rem)]">We've got a few pointers to help you along.</p>
          </div>

          <div className="flex flex-wrap gap-6 lg:gap-10 justify-center py-6 lg:py-6 text-[#777e8d] max-w-7xl mx-auto">
            {/* STEP 1 */}
            <div className="w-full sm:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)] min-w-[280px] max-w-[400px] text-center">
              <div className="flex justify-center mb-4">
                <svg width="60" height="60" className="w-[clamp(50px,8vw,60px)] h-[clamp(50px,8vw,60px)]">
                  <circle cx="50%" cy="50%" r="28" fill="#1462af" />
                  <text x="50%" y="50%" textAnchor="middle" dy=".35em" fontSize="24" fill="white" fontWeight="bold">1</text>
                </svg>
              </div>
              <h3 className="text-[clamp(0.9rem,2.5vw,0.25rem)] font-semibold mb-3">Pick a Topic & Kick Things Off</h3>
              <p className="text-[clamp(0.875rem,1.8vw,0rem)] leading-relaxed">
                Start by choosing any topic from the{" "}
                <span className="font-semibold text-[#f4833d]">"Try A Sample Text"</span>{" "}
                dropdown and hit <span className="font-semibold text-[#f4833d]">Submit.</span>
              </p>
              <p className="mt-3 text-[clamp(0.875rem,1.8vw,0rem)] leading-relaxed">
                We'll whip up a quick summary for you — just tap <span className="font-semibold">OK</span> and you're on your way!
              </p>
            </div>

            {/* STEP 2 */}
            <div className="w-full sm:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)] min-w-[280px] max-w-[400px] text-center">
              <div className="flex justify-center mb-4">
                <svg width="60" height="60" className="w-[clamp(50px,8vw,60px)] h-[clamp(50px,8vw,60px)]">
                  <circle cx="50%" cy="50%" r="28" fill="#1462af" />
                  <text x="50%" y="50%" textAnchor="middle" dy=".35em" fontSize="24" fill="white" fontWeight="bold">2</text>
                </svg>
              </div>
              <h3 className="text-[clamp(0.9rem,2.5vw,0.25rem)] font-semibold mb-3">Your Quiz Recipe Is Ready</h3>
              <p className="text-[clamp(0.875rem,1.8vw,0rem)] leading-relaxed">
                Next, you'll see the{" "}
                <span className="font-semibold text-[#f4833d]">category, sub-category,</span>{" "}
                <span className="font-semibold text-[#f4833d]">and question type </span>already set up for this sample.
              </p>
              <p className="mt-3 text-[clamp(0.875rem,1.8vw,0rem)] leading-relaxed">
                Just click <span className="font-semibold text-[#f4833d]">Proceed</span> and let the magic begin — your quiz will cook in about
                <span className="font-semibold text-[#f4833d]"> 90 seconds.</span>
              </p>
            </div>

            {/* STEP 3 */}
            <div className="w-full sm:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)] min-w-[280px] max-w-[400px] text-center">
              <div className="flex justify-center mb-4">
                <svg width="60" height="60" className="w-[clamp(50px,8vw,60px)] h-[clamp(50px,8vw,60px)]">
                  <circle cx="50%" cy="50%" r="28" fill="#1462af" />
                  <text x="50%" y="50%" textAnchor="middle" dy=".35em" fontSize="24" fill="white" fontWeight="bold">3</text>
                </svg>
              </div>
              <h3 className="text-[clamp(0.9rem,2.5vw,0.25rem)] font-semibold mb-3">Ta-da! Your Quiz Is Served</h3>
              <p className="text-[clamp(0.875rem,1.8vw,0rem)] leading-relaxed">
                Sit tight while GPT does its thing — your full question set will appear like magic.
              </p>
              <p className="mt-3 text-[clamp(0.875rem,1.8vw,0rem)] leading-relaxed">
                Love it? Great. Want to tweak it? Even better — everything is yours to edit and perfect.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="text-center text-[#777e8d] mt-[65px]">
            <p className="text-[1rem]">
              Hi there, simply <span className="font-bold">type </span>or
              <span className="font-bold"> paste </span>document here to get started.
            </p>
            <p className="text-[1rem] my-2 mt-[15px]">Or</p>
          </div>

          <div className="flex gap-10 place-content-center py-4 text-[#777e8d]">
            <div className="w-1/3"></div>

            <div className="w-1/3">
              <p>
                {showTooltip ? (
                  <Tooltip
                    title="File size should be less than 10MB."
                    arrow placement="top"
                    TransitionComponent={Zoom}
                    TransitionProps={{ timeout: 300 }}
                  >
                    <span
                      className={`flex justify-center ${
                        isSampleButtonClicked ? "cursor-not-allowed" : "cursor-pointer hover:underline"
                      } text-[#2a50cb] font-semibold`}
                      onClick={handleUploadLinkClick}
                    >
                      Upload a PDF/DOCX from your device
                    </span>
                  </Tooltip>
                ) : (
                  <span
                    className={`flex justify-center ${
                      isSampleButtonClicked ? "cursor-not-allowed" : "cursor-pointer hover:underline"
                    } text-[#2a50cb] font-semibold`}
                    onClick={handleUploadLinkClick}
                  >
                    Upload a PDF/DOCX from your device
                  </span>
                )}
                <span>
                  <input
                    type="file"
                    style={{ display: "none" }}
                    ref={fileInputRef}
                    onChange={handleFileInputChange}
                    disabled={isSampleButtonClicked || isFileUploaded}
                  />
                </span>
              </p>
            </div>

            <div className="w-1/3"></div>
          </div>
        </>
      )}
    </div>
  );
};

export default HelpingPointer;
